import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ImageModal from '../shared/ImageModal';
import LazyImage from '../shared/LazyImage';
import useCatalogData, { ProcessedImage } from '../../hooks/useCatalogData';

const ImagePreviewGallery: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { images, loading } = useCatalogData(i18n.language);
  const [selectedImage, setSelectedImage] = useState<ProcessedImage | null>(null);

  // Show first 3 images as preview for immediate visual impact
  const previewImages = images.slice(0, 3);

  const handleImageClick = (image: ProcessedImage) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <section className="py-24 bg-dark-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="w-24 h-px bg-gold/30 mx-auto mb-8"></div>
              <div className="h-8 bg-dark-surface rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-dark-surface rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="preview-gallery" className="py-24 bg-dark-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Elegant separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '120px', opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/50 mx-auto mb-8"
          />

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-light text-cream mb-6 tracking-wide">
            {t('gallery.title')}
          </h2>
          <p className="text-lg text-cream-dark max-w-3xl mx-auto font-light leading-relaxed">
            {t('gallery.subtitle')}
          </p>

          {/* Bottom separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '80px', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/30 mx-auto mt-8"
          />
        </motion.div>

        {/* Gallery grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {previewImages.map((image, index) => (
            <motion.div
              key={image.id}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => handleImageClick(image)}
            >
              <div className="relative overflow-hidden bg-dark-surface border border-dark-border/20 hover:border-gold/30 transition-all duration-500 hover-lift">
                {/* Lazy Loaded Image */}
                <div className="aspect-[3/4] relative">
                  <LazyImage
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-primary/90 via-dark-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="font-playfair text-lg text-cream mb-2 group-hover:text-gold/90 transition-colors duration-300">
                        {image.title}
                      </h3>
                      
                      <div className="w-12 h-px bg-gold/50 mb-4 group-hover:w-16 group-hover:bg-gold/70 transition-all duration-300"></div>
                      
                      <div className="flex items-center text-gold text-sm">
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        <span className="font-light tracking-wider uppercase text-xs">
                          {t('gallery.viewDetails')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View Full Gallery Button */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <a
            href="#full-gallery"
            className="group inline-block px-12 py-4 border border-gold/30 hover:border-gold hover:bg-gold/5 transition-all duration-500"
          >
            <span className="text-cream font-light tracking-widest uppercase text-sm">
              {t('gallery.loadMore')} ({images.length} {t('gallery.totalImages')})
            </span>
            <svg
              className="inline-block ml-3 w-4 h-4 group-hover:translate-x-2 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </motion.div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          isOpen={!!selectedImage}
          onClose={handleCloseModal}
          src={selectedImage.src}
          alt={selectedImage.title}
          title={selectedImage.title}
          description={selectedImage.description}
          imageId={selectedImage.id}
        />
      )}
    </section>
  );
};

export default ImagePreviewGallery;